import React from "react";
import { useTheme } from "styled-components";

import { NAVBAR } from "@config/testIds/navbar";

import { Arrow as ArrowIcon } from "@ui/Assets/MyIcons";
import { TestWrapper } from "@ui/Atoms/TestWrapper";

import { DrawerManager } from "@features/DrawerManager/DrawerManager.www";

import { useTranslate } from "@hooks/useTranslate";
import { useAuth } from "@hooks/useAuth";
import { useTelemetry } from "@hooks/useTelemetry";
import { useBreakpoint } from "@hooks/useBreakpoint";

import * as VerticalNav from "../VerticalNav";
import { useNavBar } from "../hooks/useNavBar";
import { useLangNav } from "../hooks/useLangNav";
import { useMainNav, scrollToHandler } from "../hooks/useMainNav";
import { CustomDrawerHeader } from "../CustomDrawerHeader";

import { Icon } from "./MobileMenu.icon";
import * as Styled from "./MobileMenu.styled";

const menuDrawerId = "main-menu";
const languageDrawerId = "language-menu";

export const MobileMenu: React.FC = () => {
  const theme = useTheme();
  const { isUserAgent } = useAuth();
  const { elementClick } = useTelemetry();
  const { mobile, largeMobile } = useBreakpoint();
  const translate = useTranslate("batmaid_pwa_navigation");
  const { showDrawer, hideDrawer } = DrawerManager.useManager();
  const { language } = useNavBar();
  const { links: langLinks, getActiveLink } = useLangNav();
  const navLinks = useMainNav(language);

  const iconSize = theme.icons.sizes.base_x3;
  const activeLangLink = getActiveLink(language);

  const isMobile = mobile || largeMobile;

  const clickBurger = () => {
    showDrawer(menuDrawerId);

    elementClick("", {
      buttonId: "burgerMenu",
    });
  };

  const clickLangButton = () => {
    showDrawer(languageDrawerId);

    elementClick(activeLangLink.label, {
      buttonId: "openLangMenu",
    });
  };

  const clickMainNavLink = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const isScrolling = scrollToHandler(event);

    if (isScrolling) {
      hideDrawer(menuDrawerId);
    }

    const $link = event.currentTarget;

    elementClick($link.textContent || "", {
      buttonId: $link.getAttribute("data-testid") || "",
    });
  };

  React.useEffect(() => {
    const hideAllDrawers = () => {
      hideDrawer(menuDrawerId);
      hideDrawer(languageDrawerId);
    };

    if (!isMobile) {
      hideAllDrawers();
    }

    return () => {
      hideAllDrawers();
    };
  }, [isMobile]);

  return (
    <React.Fragment>
      <TestWrapper testId={NAVBAR.HAMBURGER_DROPDOWN}>
        <Styled.BurgerButton onClick={clickBurger}>
          <Icon />
        </Styled.BurgerButton>
      </TestWrapper>

      <DrawerManager
        id={menuDrawerId}
        testId="mainMenu"
        closeTestId="closeMainMenu"
        customHeader={
          <CustomDrawerHeader
            crossButton={{
              testId: "crossButton",
              onClick: () => {
                hideDrawer(menuDrawerId);
                elementClick("", { buttonId: "closeMainMenu" });
              },
            }}
          />
        }
      >
        <VerticalNav.Nav>
          {!isUserAgent && (
            <React.Fragment>
              {navLinks.map(link => (
                <TestWrapper key={link.testId} testId={link.testId}>
                  <VerticalNav.Link
                    as="a"
                    href={link.href}
                    target={link.target}
                    onClick={clickMainNavLink}
                    $style="medium_18"
                  >
                    {link.label}
                  </VerticalNav.Link>
                </TestWrapper>
              ))}

              <Styled.GridHorizontalRule />
            </React.Fragment>
          )}

          {!!activeLangLink && (
            <TestWrapper testId={NAVBAR.LANGUAGE_TRIGGER}>
              <Styled.OpenLangDrawerButton onClick={clickLangButton}>
                <VerticalNav.CodeBlock>
                  {activeLangLink.code}
                </VerticalNav.CodeBlock>

                <Styled.OpenLangDrawerButtonLabel $style="medium_18">
                  {activeLangLink.label}
                </Styled.OpenLangDrawerButtonLabel>
                <ArrowIcon width={iconSize} />
              </Styled.OpenLangDrawerButton>
            </TestWrapper>
          )}
        </VerticalNav.Nav>
      </DrawerManager>

      <DrawerManager
        id={languageDrawerId}
        testId={NAVBAR.LANGUAGE_DROPDOWN}
        closeTestId="closeLangMenu"
        customHeader={
          <CustomDrawerHeader
            arrowButton={{
              testId: "arrowButton",
              onClick: () => {
                hideDrawer(languageDrawerId);
                elementClick("", { buttonId: "closeLangMenu" });
              },
            }}
            crossButton={{
              testId: "crossButton",
              onClick: () => {
                hideDrawer(menuDrawerId);
                hideDrawer(languageDrawerId);
                elementClick("", {
                  buttonId: "closeAllDrawers",
                });
              },
            }}
          >
            {translate("choose_language")}
          </CustomDrawerHeader>
        }
      >
        <VerticalNav.Nav>
          {langLinks.map(link => (
            <TestWrapper key={link.testId} testId={link.testId}>
              <VerticalNav.Link
                as="a"
                href={link.href}
                onClick={link.clickHandler}
                $style="medium_18"
              >
                <VerticalNav.CodeBlock>{link.code}</VerticalNav.CodeBlock>
                <span>{link.label}</span>
              </VerticalNav.Link>
            </TestWrapper>
          ))}
        </VerticalNav.Nav>
      </DrawerManager>
    </React.Fragment>
  );
};
